/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderFormSchemaFactory, BuilderUserContext, FormRootSchema,
  FormState, FormStateSchema,
  whenAuRegion,
  whenCorporateView,
  whenCreateMode,
  whenCreateOrEditMode,
  whenEditOrViewMode,
  whenMasterZoneView,
  whenNzRegion,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/builder-shared';
import {
  OfficeMeasurement,
  OfficeMeasurementDefault,
  OfficeStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { DateTimeTypes, isRouteForCreationMode, MaskedTypes, NumberTypes, TextTypes, UserContext } from '@gh/common-shared';
import { FormMode, getControlValue, setControlValue } from '@gh/core-ui';
import { mapValues } from 'lodash';
import {
  isParentOfficeImportTemplate,
  OfficeDetails,
  OfficeImportTemplate,
} from '../../resources/office-details.model';

@Injectable()
export class OfficeDetailsFormState extends FormState<OfficeDetails> {
  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  get submitValue(): OfficeDetails {
    return this.group.getRawValue();
  }

  get templateFlatHierarchy(): Hash<OfficeImportTemplate> {
    return getControlValue(this.group, 'templateFlatHierarchy');
  }

  refreshFinancialYearInformationSchema(): void {
    this.get('currentFinYear').updateSchema({ enabled: false });
    this.get('lastMonthFinYear').updateSchema({ enabled: false });
    this.get('conversionDate').updateSchema({ enabled: false });
  }

  updateEmail(): void {
    setControlValue(this.group, 'email', getControlValue(this.group, 'systemName'));
  }

  updateLicenseState(): void {
    this.get('licenseNo').updateSchema({
      enabled: getControlValue(this.group, 'applyLicense'),
    });
  }

  updateMeasurementState(): void {
    const measurement = this.get('measurement');
    const measurementDefault = this.get('measurementDefault');
    const isMetric = measurement.value === OfficeMeasurement.Metric;

    measurementDefault.updateSchema({
      required: isMetric,
      visible: isMetric,
    });

    measurementDefault.setValue({
      [OfficeMeasurement.Metric]: OfficeMeasurementDefault.Millimeter,
      [OfficeMeasurement.Imperial]: OfficeMeasurementDefault.Foot,
    }[measurement.value]);
  }

  resetTemplates(): void {
    const templates = this.templateFlatHierarchy;
    setControlValue(
      this.group,
      'templateFlatHierarchy',
      mapValues(templates, (template) => ({ ...template, checked: false })));
  }

  updateTimeZone(): void {
    setControlValue(this.group, 'timeZone', getControlValue(this.group, 'masterZone').timeZone);
  }

  /**
   * Checks if some (but no all) children of given template are selected
   * @param name
   */
  isTemplatePartlyChecked(name: string): boolean {
    const templates = this.templateFlatHierarchy;
    const template = templates[name];
    if (isParentOfficeImportTemplate(template)) {
      const numberOfSelectedChildren = template.childrenNames
        .filter((childName) => templates[childName].checked).length;
      return numberOfSelectedChildren > 0 && numberOfSelectedChildren < template.childrenNames.length;
    } else {
      return false;
    }
  }

  /**
   * Checks if given template should be checked.
   * @param name
   */
  isTemplateChecked(name: string): boolean {
    const templates = this.templateFlatHierarchy;
    const template = templates[name];
    return isParentOfficeImportTemplate(template)
      ? template.childrenNames.every((childName) => templates[childName].checked)
      : template.checked;
  }

  /**
   * Marks/Unmarks given template.
   * This method traverses given template and its relative to mark/unmark them.
   * @param rootName
   * @param checked
   */
  markChildTemplates(rootName: string, checked: boolean): void {
    const addToTraverse = (name: string) => {
      if (!visited[name]) {
        stack.push(name);
        visited[name] = true;
      }
    };
    const templates = this.templateFlatHierarchy;
    const visited = {};
    const stack = [rootName];
    while (stack.length) {
      const name = stack.pop()!; // tslint:disable-line:no-non-null-assertion
      const current = templates[name];
      if (!isParentOfficeImportTemplate(current)) {
        current.checked = checked;
      }
      if (checked) {
        current.nextNames.forEach(addToTraverse);
      } else {
        current.previousNames.forEach(addToTraverse);
      }
      current.childrenNames.forEach(addToTraverse);
    }
  }

  enableFinancialInformationControls(): void {
    this.enablePartial(['currentFinYear', 'lastMonthFinYear', 'conversionDate']);
  }

  onRecalculateAssignmentFee(): void {
    const onSellDate = this.get('billing.onSellDate').value;
    const contractSignedDate = this.get('billing.contractSignedDate').value;
    const saleAmount = this.get('billing.saleAmount').value;

    const within18Months = this.get('billing.within18Months').value;
    const within24Months = this.get('billing.within24Months').value;
    const within36Months = this.get('billing.within36Months').value;
    const after36Months = this.get('billing.after36Months').value;

    const monthsBtwnDates = (startDate: Date, endDate: Date) => {
      if (!startDate || !endDate) {
        return null;
      }
      startDate = new Date(startDate);
      endDate = new Date(endDate);
      return Math.abs(
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        endDate.getMonth() -
        startDate.getMonth());
    };

    const months = monthsBtwnDates(onSellDate, contractSignedDate);

    if (!months) {
      setControlValue(this.group, 'billing.assignmentFee', null);
    } else {
      if (months <= 18) {
        setControlValue(this.group, 'billing.assignmentFee', saleAmount * within18Months / 100);
      } else {
        if (months <= 24) {
          setControlValue(this.group, 'billing.assignmentFee', saleAmount * within24Months / 100);
        } else {
          if (months <= 36) {
            setControlValue(this.group, 'billing.assignmentFee', saleAmount * within36Months / 100);
          } else {
            if (months > 36) {
              setControlValue(this.group, 'billing.assignmentFee', saleAmount * after36Months / 100);
            }
          }
        }
      }
    }
  }

  onRecalculateRenewalDate(): void {
    const term = this.get('billing.term').value;
    const currentAgreement = this.get('billing.currentAgreement').value;
    const originalCommencement = this.get('billing.originalCommencement').value;
    if (!currentAgreement) {
      setControlValue(this.group, 'billing.renewalDate',
                      // tslint:disable-next-line:max-line-length
                      new Date(originalCommencement.getFullYear() + term, originalCommencement.getMonth(), originalCommencement.getDate()));
    } else {
      setControlValue(this.group, 'billing.renewalDate',
                      // tslint:disable-next-line:max-line-length
                      new Date(currentAgreement.getFullYear() + term, currentAgreement.getMonth(), currentAgreement.getDate()));
    }
  }

  protected buildSchema(details: OfficeDetails, { root, group }: FormStateSchema): FormRootSchema {
    let mode: FormMode;
    if (isRouteForCreationMode(this.route)) {
      mode = FormMode.CreateMode;
    } else if (details.status === OfficeStatus.Deactivated) {
      mode = FormMode.ViewMode;
    } else {
      mode = FormMode.EditMode;
    }

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        name: { type: TextTypes.OfficeName },
        status: {
          visible: whenEditOrViewMode(),
        },
        type: {
          required: true,
          enabled: [whenCreateMode(), whenMasterZoneView()],
        },
        masterZone: {
          required: true,
          visible: whenCorporateView(),
          enabled: whenCreateMode(),
        },
        typeOfBusiness: void 0,
        formedDate: { type: DateTimeTypes.ShortDate },
        companyName: {
          required: true,
        },
        displayName: {
          required: true,
        },
        systemName: {
          required: true,
          enabled: whenCreateMode(),
          type: MaskedTypes.GoogleGroupName,
        },
        taxId: {
          visible: whenUsRegion(),
          type: MaskedTypes.TaxId,
        },
        abn: {
          visible: whenAuRegion(),
          type: MaskedTypes.Abn,
        },
        acn: {
          visible: whenAuRegion(),
          type: MaskedTypes.Acn,
        },
        tradingAs: {
          required: true,
        },
        gst: {
          visible: whenNzRegion(),
          type: MaskedTypes.Gst,
        },
        street1: {
          required: true,
        },
        street2: void 0,
        city: {
          required: true,
        },
        state: {
          required: true,
          visible: whenUsOrAuRegion(),
        },
        postCode: {
          required: true,
          type: MaskedTypes.Zip,
        },
        applyLicense: void 0,
        publishToWeb: void 0,
        licenseNo: {
          enabled: details.applyLicense,
        },
        email: {
          enabled: false,
        },
        phoneOffice: void 0,
        fax: void 0,
        phoneMobile: void 0,
        measurement: {
          required: true,
          enabled: whenCreateMode(),
        },
        measurementDefault: {
          required: details.measurement === OfficeMeasurement.Metric,
          visible: details.measurement === OfficeMeasurement.Metric,
          enabled: whenCreateMode(),
        },
        sourceOffice: {
          enabled: whenCreateMode(),
        },
        templateFlatHierarchy: void 0,
        templates: void 0,
        timeZone: { required: true, enabled: whenCreateMode() },
        currentFinYear: { enabled: whenCreateMode(), required: true },
        lastMonthFinYear: { enabled: whenCreateMode(), required: true },
        conversionDate: { enabled: whenCreateMode(), required: true },
        billing: group({
          children: {
            contractSignedDate: { required: true, type: DateTimeTypes.ShortDate },
            depositDate: { required: true, type: DateTimeTypes.ShortDate },
            originalCommencement: { required: true, type: DateTimeTypes.ShortDate },
            currentAgreement: { type: DateTimeTypes.ShortDate },
            renewalDate: { required: true, type: DateTimeTypes.ShortDate },
            officeClosed: { type: DateTimeTypes.ShortDate },
            term: { required: true, type: NumberTypes.Quantity },
            taxRate: { required: true, type: NumberTypes.Percents },
            initialFranchiseFee: { type: NumberTypes.SignedPrice },
            franchiseFee: { required: true, type: NumberTypes.Percents },
            variationFee: { type: NumberTypes.Percents, enabled: this.isUsersFromOtherOfficesAccessAllow() },
            within18Months: { type: NumberTypes.Percents },
            within24Months: { type: NumberTypes.Percents },
            within36Months: { type: NumberTypes.Percents },
            after36Months: { type: NumberTypes.Percents },
            onSellDate: { type: DateTimeTypes.ShortDate },
            saleAmount: { type: NumberTypes.SignedPrice },
            assignmentFee: { type: NumberTypes.SignedPrice },
          },
        }),
      },
    });
  }
  private isUsersFromOtherOfficesAccessAllow(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master);
  }
}
